import type { ReactNode } from "react";
import "@/styles/v47-pages.css";

/**
 * One headline card (prototype v45/v46 `headCard`). Either a single value (`v`) or a from → to
 * pair (`from`, `to`): the figure you're on now, struck through, an arrow, and what Abi's plan
 * makes of it, on one line, with the unit written once, on `to` ("$1,000 → $3,034
 * savings/month"). `lead` starts the subtext in green and a size up ("+$2,034 savings/month"),
 * then `s` follows in plain text.
 */
export type HeadCard = {
  /** The eyebrow ("This month", "With Abi’s plan"). */
  k: ReactNode;
  v?: ReactNode;
  from?: ReactNode;
  to?: ReactNode;
  /** Colours the value (or `to`): saved/gain `ok`, close `warn`, overspent/owed `debt`. */
  tone?: "ok" | "warn" | "debt";
  /** A value that doesn't exist yet ("Waiting on your accounts"): smaller and muted. */
  pending?: boolean;
  /** The Abi card: gold border and wash. */
  abi?: boolean;
  lead?: ReactNode;
  /** The lead's colour: green by default; `debt` when the move is the wrong way, `flat` for none. */
  leadTone?: "ok" | "debt" | "flat";
  s?: ReactNode;
  testId?: string;
};

/**
 * The headline numbers (v45 `headDraw`): three cards in the same three places on every page —
 * where you are now, where that is heading, and what Abi's plan makes of it (four on
 * Achievements: `four` is set automatically past three cards). `id` is the prototype's container
 * id (`hd-cf`, `hd-debt`, ...), kept because the page guides anchor to it.
 */
export function Headline({
  id,
  cards,
  testId,
}: {
  id: string;
  cards: HeadCard[];
  testId?: string;
}) {
  return (
    <div className={`hdrow${cards.length > 3 ? " four" : ""}`} id={id} data-testid={testId ?? id}>
      {cards.map((c, i) => (
        <div className={`hdc${c.abi ? " abi" : ""}`} key={i} data-testid={c.testId}>
          <div className="eyebrow">{c.k}</div>
          {c.to !== undefined ? (
            <div className="hdpair">
              <span className="hdwas">{c.from}</span>
              <span className="hdarr" aria-hidden="true">
                &rarr;
              </span>
              <span className={`hdis${c.tone ? ` ${c.tone}` : ""}`}>{c.to}</span>
            </div>
          ) : (
            <div className={`hdv${c.tone ? ` ${c.tone}` : ""}${c.pending ? " pend" : ""}`}>
              {c.v}
            </div>
          )}
          {(c.s !== undefined || c.lead !== undefined) && (
            <p className="hdsub">
              {c.lead !== undefined && (
                <>
                  <span
                    className={`lead${c.leadTone && c.leadTone !== "ok" ? ` ${c.leadTone}` : ""}`}
                  >
                    {c.lead}
                  </span>{" "}
                </>
              )}
              {c.s}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
